import React, { useEffect, useState, useCallback } from 'react';

interface DevToolsSecurityShieldProps {
  children: React.ReactNode;
}

export const DevToolsSecurityShield: React.FC<DevToolsSecurityShieldProps> = ({ children }) => {
  // Admin bypass mode: when true, security restrictions (like right click blocking) are bypassed for admin
  const [isAdminBypass, setIsAdminBypass] = useState<boolean>(() => {
    try {
      return localStorage.getItem('vss_admin_security_bypass') === 'true';
    } catch {
      return false;
    }
  });

  const [toastMessage, setToastMessage] = useState<string | null>(null);

  const showToast = useCallback((msg: string) => {
    setToastMessage(msg);
    const timer = setTimeout(() => {
      setToastMessage(null);
    }, 2000);
    return () => clearTimeout(timer);
  }, []);

  // Keyboard shortcut to toggle Admin Security Bypass mode: Ctrl + Alt + Shift + A
  useEffect(() => {
    const handleAdminToggle = (e: KeyboardEvent) => {
      if (e.ctrlKey && e.altKey && e.shiftKey && e.key.toLowerCase() === 'a') {
        e.preventDefault();
        setIsAdminBypass((prev) => {
          const next = !prev;
          try {
            localStorage.setItem('vss_admin_security_bypass', String(next));
          } catch {}
          showToast(next ? '🔓 Admin Mode: Security Bypass Enabled' : '🔒 Public Protection Mode Active');
          return next;
        });
      }
    };

    window.addEventListener('keydown', handleAdminToggle);
    return () => window.removeEventListener('keydown', handleAdminToggle);
  }, [showToast]);

  // Public Protection Handlers: Silently block right-click and DevTools hotkeys without interrupting app usage
  useEffect(() => {
    // If Admin bypass is ON, do not block right click or shortcuts
    if (isAdminBypass) return;

    const handleContextMenu = (e: MouseEvent) => {
      // Prevent opening browser inspect context menu on all buttons, panels, window elements
      e.preventDefault();
      e.stopPropagation();
      return false;
    };

    const handleKeyDown = (e: KeyboardEvent) => {
      const isMac = navigator.platform.toUpperCase().indexOf('MAC') >= 0;
      const ctrlOrCmd = isMac ? e.metaKey : e.ctrlKey;
      const altOrOpt = e.altKey;
      const shift = e.shiftKey;
      const key = e.key.toLowerCase();
      const keyCode = e.keyCode;

      // F12 Key
      if (keyCode === 123 || key === 'f12') {
        e.preventDefault();
        e.stopPropagation();
        return false;
      }

      // Shift + F10 (Context menu hotkey)
      if (shift && (keyCode === 121 || key === 'f10')) {
        e.preventDefault();
        e.stopPropagation();
        return false;
      }

      // Ctrl + Shift + I / Cmd + Option + I (Inspect/DevTools)
      if (ctrlOrCmd && (shift || altOrOpt) && key === 'i') {
        e.preventDefault();
        e.stopPropagation();
        return false;
      }

      // Ctrl + Shift + J / Cmd + Option + J (Console)
      if (ctrlOrCmd && (shift || altOrOpt) && key === 'j') {
        e.preventDefault();
        e.stopPropagation();
        return false;
      }

      // Ctrl + Shift + C / Cmd + Option + C (Inspect Element)
      if (ctrlOrCmd && (shift || altOrOpt) && key === 'c') {
        e.preventDefault();
        e.stopPropagation();
        return false;
      }

      // Ctrl + Shift + K (Firefox Console)
      if (ctrlOrCmd && shift && key === 'k') {
        e.preventDefault();
        e.stopPropagation();
        return false;
      }

      // Ctrl + U / Cmd + Option + U (View Source)
      if (ctrlOrCmd && (key === 'u' || (altOrOpt && key === 'u'))) {
        e.preventDefault();
        e.stopPropagation();
        return false;
      }

      // Ctrl + S / Cmd + S (Save Page)
      if (ctrlOrCmd && key === 's') {
        e.preventDefault();
        e.stopPropagation();
        return false;
      }
    };

    const handleDragStart = (e: DragEvent) => {
      if ((e.target as HTMLElement)?.tagName === 'IMG') {
        e.preventDefault();
      }
    };

    // Attach listeners with capturing phase true so no element inside the app opens context menu
    window.addEventListener('contextmenu', handleContextMenu, true);
    window.addEventListener('keydown', handleKeyDown, true);
    window.addEventListener('dragstart', handleDragStart, true);

    return () => {
      window.removeEventListener('contextmenu', handleContextMenu, true);
      window.removeEventListener('keydown', handleKeyDown, true);
      window.removeEventListener('dragstart', handleDragStart, true);
    };
  }, [isAdminBypass]);

  return (
    <>
      {/* Toast notification when admin mode is toggled */}
      {toastMessage && (
        <div className="fixed bottom-5 right-5 z-[9999999] bg-zinc-900/95 text-zinc-100 border border-indigo-500/50 px-4 py-2.5 rounded-xl shadow-2xl text-xs font-semibold backdrop-blur-md animate-fade-in pointer-events-none">
          {toastMessage}
        </div>
      )}

      {/* The main software interface renders 100% normally and fully functional */}
      {children}
    </>
  );
};
